import { useCallback, useMemo, useRef, useState } from 'react'
import { Box, Paper } from '@mui/material'
import { useDiRef } from '@/common/react/di-hooks.ts'
import { useBehaviorObservable } from '@/common/react/observable-hooks.ts'
import {
  collectionPageConfig as config,
  getMinColWidth,
} from 'objects/collection-view/collection-page-config.ts'
import { SkeletonRow } from '@/objects/collection-view/grid/GridRow.tsx'
import type { PropertyColumn } from '@/objects/collection-view/grid/PropertyColumn.tsx'
import type { CollectionObjectsService } from '@/objects/services/CollectionObjectsService.ts'
import type { CollectionViewsService } from '@/objects/services/CollectionViewsService.ts'
import { CollectionGroup } from 'objects/collection-view/grid/CollectionGroup.tsx'
import { useCollectionViewContext } from '@/objects/collection-view/top-bar/CollectionViewPageContext'
import { logJson } from '@/common/errors/log-utils.ts'
import { useTutorialTarget } from '@/tutorial/TutorialContext'

const headerCellStyle = {
  position: 'relative' as const,
  padding: `6px ${config.cellPaddingX}px`,
  fontWeight: 600,
  fontSize: 13,
  borderBottom: '1px solid var(--mui-palette-divider, #e0e0e0)',
  userSelect: 'none' as const,
  overflow: 'visible',
  display: 'flex',
  alignItems: 'center',
}

export function CollectionGrid() {
  const { collectionId, viewSettings, objectType: type } = useCollectionViewContext()
  const objectsService = useDiRef<CollectionObjectsService>('CollectionObjectsService')
  const viewsService = useDiRef<CollectionViewsService>('CollectionViewsService')
  const tabObjectsObs = type
    ? objectsService.getActiveTabObjects$(collectionId, type.id)
    : undefined
  const tabObjects = useBehaviorObservable(tabObjectsObs)

  const propDefs = type?.propertyDefinitions ?? []
  const viewColumns = viewSettings?.columns ?? []

  const orderedColumns = useMemo(
    () =>
      viewColumns
        .filter((vc) => vc.isVisible)
        .map((vc) => ({
          propDef: propDefs.find((p) => p.id === vc.propertyDefinitionId),
          settings: vc,
        }))
        .filter((c): c is PropertyColumn => c.propDef != null),
    [viewColumns, propDefs],
  )

  const dragColIndex = useRef<number | null>(null)
  const [resizeOverride, setResizeOverride] = useState<number[] | null>(null)

  const onDragStart = useCallback((displayIndex: number) => {
    dragColIndex.current = displayIndex
  }, [])

  const onDrop = useCallback(
    (targetDisplayIndex: number) => {
      const src = dragColIndex.current
      dragColIndex.current = null
      if (src == null || src === targetDisplayIndex || src === 0 || targetDisplayIndex === 0) return

      const moved = orderedColumns[src]
      const reordered = orderedColumns.filter((_, i) => i !== src)
      reordered.splice(targetDisplayIndex, 0, moved)

      const updates = reordered.map((col, idx) => ({
        propertyDefinitionId: col.propDef.id,
        order: idx,
      }))
      viewsService.updateColumns(collectionId, updates)
    },
    [orderedColumns, collectionId, viewsService],
  )

  const onResizeMouseDown = useCallback(
    (displayIndex: number, e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      const leftCol = orderedColumns[displayIndex]
      const startX = e.clientX
      const headerEl = e.currentTarget.parentElement!
      const startPxWidth = headerEl.getBoundingClientRect().width
      const weightPerPx = leftCol.settings.widthWeight / startPxWidth
      const startWeights = orderedColumns.map((c) => c.settings.widthWeight)
      let latestWeights = startWeights

      const computeWeights = (rawDeltaW: number): number[] => {
        const weights = [...startWeights]
        if (rawDeltaW >= 0) {
          // grow left col, cascade shrink into right columns
          let remaining = rawDeltaW
          for (let i = displayIndex + 1; i < orderedColumns.length && remaining > 0; i++) {
            const minW = getMinColWidth(orderedColumns[i].propDef.type) * weightPerPx
            const available = Math.max(0, weights[i] - minW)
            const take = Math.min(remaining, available)
            weights[i] -= take
            remaining -= take
          }
          weights[displayIndex] += rawDeltaW - remaining
        } else {
          // shrink left col, grow adjacent right — cap at left col min
          const minLeftW = getMinColWidth(leftCol.propDef.type) * weightPerPx
          const clampedDelta = Math.max(rawDeltaW, minLeftW - startWeights[displayIndex])
          weights[displayIndex] += clampedDelta
          weights[displayIndex + 1] -= clampedDelta
        }
        return weights
      }

      const onMove = (ev: MouseEvent) => {
        const rawDeltaW = (ev.clientX - startX) * weightPerPx
        latestWeights = computeWeights(rawDeltaW)
        setResizeOverride(latestWeights)
      }

      const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        setResizeOverride(null)
        const updates = orderedColumns
          .map((c, i) => ({ propertyDefinitionId: c.propDef.id, widthWeight: latestWeights[i] }))
          .filter((_, i) => latestWeights[i] !== startWeights[i])
        if (updates.length > 0) viewsService.updateColumns(collectionId, updates)
      }

      window.addEventListener('mousemove', onMove)
      window.addEventListener('mouseup', onUp)
    },
    [orderedColumns, collectionId, viewsService],
  )

  const collectionGridRef = useTutorialTarget('collection-grid')

  if (orderedColumns.length === 0) return null

  const gridTemplateColumns = orderedColumns
    .map((c, i) => {
      const w = resizeOverride ? resizeOverride[i] : c.settings.widthWeight
      return `minmax(${getMinColWidth(c.propDef.type)}px, ${w}fr)`
    })
    .join(' ')

  return (
    <Paper
      ref={collectionGridRef}
      sx={{
        flex: 1,
        overflowX: 'clip',
        overflowY: 'auto',
        height: '100%',
        margin: 1.5,
        backgroundColor: 'background.default',
        border: (theme) =>
          theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : 'none',
      }}
    >
      <div
        style={{
          display: 'grid',
          gridTemplateColumns,
          width: '100%',
          position: 'sticky',
          top: 0,
          zIndex: 1,
          overflowX: 'clip',
        }}
      >
        {/* Sticky column headers */}
        {orderedColumns.map((col, displayIdx) => (
          <Box
            key={col.propDef.id}
            draggable={displayIdx !== 0}
            onDragStart={displayIdx !== 0 ? () => onDragStart(displayIdx) : undefined}
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(displayIdx)}
            style={{ ...headerCellStyle, cursor: displayIdx === 0 ? 'default' : 'grab' }}
            sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'action.hover' } }}
          >
            <span
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                minWidth: 0,
              }}
            >
              {col.propDef.name}
            </span>
            {displayIdx < orderedColumns.length - 1 && (
              <Box
                onMouseDown={(e) => onResizeMouseDown(displayIdx, e)}
                sx={{
                  position: 'absolute',
                  right: -config.draggableSeamWidth / 2,
                  top: 0,
                  bottom: 0,
                  width: config.draggableSeamWidth,
                  cursor: 'col-resize',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover .col-sep': { opacity: 1 },
                }}
              >
                <Box
                  className="col-sep"
                  sx={{
                    width: 4,
                    height: '80%',
                    borderRadius: 1,
                    bgcolor: 'primary.main',
                    pointerEvents: 'none',
                    opacity: 0,
                    transition: 'opacity 0.15s',
                  }}
                />
              </Box>
            )}
          </Box>
        ))}
      </div>

      {/* Groups or skeleton */}
      {tabObjects === undefined ? (
        <div>
          {Array.from({ length: config.skeletonRowCount }, (_, rowIdx) => (
            <SkeletonRow
              key={rowIdx}
              rowIdx={rowIdx}
              orderedColumns={orderedColumns}
              gridTemplateColumns={gridTemplateColumns}
            />
          ))}
        </div>
      ) : (
        tabObjects.groups.map((group, i) => (
          <CollectionGroup
            key={group.groupByOption?.value ?? i}
            group={group}
            hideHeader={!tabObjects.isGrouped}
            orderedColumns={orderedColumns}
            gridTemplateColumns={gridTemplateColumns}
          />
        ))
      )}
    </Paper>
  )
}
